import dayjs from "dayjs";
import { CUSTOM_VALUE, DATE_SELECTION_OPTIONS, DateFormat } from "../../../../constants/dateFormat";
import { FilterModel } from "../../../../Models/applicationModels";
import { FilterChipData } from "../../../../Components/FilterShowcase/filterShowcase.models";
import { FilterOption } from "../../../../Components/FilterPopup/filterPopup.models";
import { APPLICATION_DETAILS_LABEL_MAP } from "../../../../utils/constants/applicationDetailsBifurcation";
import { GetFormatTitle, GetSplitAndTrim } from "../../../../Components/tableComponent/getTableColumn/getFormattedData";
import { getDateRangeFromCreatedAt } from "../../../../utils/getFormattedTime";
import {
    BETWEEN_OPERATOR,
    CREATED_ON_KEY,
    DEFAULT_INSIGHTS_DATE_RANGE,
    IN_OPERATOR,
    LIKE_OPERATOR,
} from "./insightsHeader.constants";
import { InsightsDateRange, InsightsUiFieldFilter, InsightsUiFilters } from "./insightsHeader.models";

export const cloneInsightsUiFilters = (filters: InsightsUiFilters): InsightsUiFilters =>
    Object.fromEntries(
        Object.entries(filters).map(([field, value]) => [field, { ...value, selectValues: value.selectValues ? [...value.selectValues] : undefined }])
    );

const getFilterFieldLabel = (field: string): string =>
    APPLICATION_DETAILS_LABEL_MAP[field] ?? GetFormatTitle(field);

const isInsightsFieldActive = (filter: InsightsUiFieldFilter | undefined): boolean => {
    if (!filter) return false;

    const operator = filter.operator.toLowerCase();
    if (operator === LIKE_OPERATOR) {
        return Boolean(filter.textValue?.trim());
    }
    if (operator === IN_OPERATOR) {
        return Boolean(filter.selectValues?.length);
    }
    if (operator === BETWEEN_OPERATOR) {
        if (!filter.datePreset) return false;
        if (filter.datePreset === CUSTOM_VALUE) {
            return Boolean(filter.customStartDate && filter.customEndDate);
        }
        return true;
    }
    return false;
};

export const countActiveInsightsFilters = (filters: InsightsUiFilters): number =>
    Object.values(filters).reduce(
        (count, filter) => (isInsightsFieldActive(filter) ? count + 1 : count),
        0
    );

export const sanitizeInsightsUiFilters = (filters: InsightsUiFilters): InsightsUiFilters => {
    const next: InsightsUiFilters = {};

    Object.entries(filters).forEach(([field, value]) => {
        const sanitized: InsightsUiFieldFilter = {
            ...value,
            textValue: value.textValue?.trim() || undefined,
            selectValues: value.selectValues?.length ? value.selectValues : undefined,
        };

        if (
            sanitized.datePreset === CUSTOM_VALUE &&
            (!sanitized.customStartDate || !sanitized.customEndDate)
        ) {
            sanitized.datePreset = undefined;
            sanitized.customStartDate = null;
            sanitized.customEndDate = null;
        }

        if (!isInsightsFieldActive(sanitized)) {
            return;
        }

        next[field] = sanitized;
    });

    return next;
};

export const toInsightsFilterModels = (
    uiFilters: InsightsUiFilters,
    availableFilters: FilterModel[]
): FilterModel[] => {
    const result: FilterModel[] = [];

    availableFilters.forEach((staticFilter) => {
        if (staticFilter.field === CREATED_ON_KEY) {
            return;
        }
        const uiFilter = uiFilters[staticFilter.field];
        if (!uiFilter || !isInsightsFieldActive(uiFilter)) {
            return;
        }

        const operator = staticFilter.operator.toLowerCase();

        if (operator === LIKE_OPERATOR && uiFilter.textValue) {
            result.push({
                field: staticFilter.field,
                operator: staticFilter.operator,
                value: uiFilter.textValue,
            });
            return;
        }

        if (operator === IN_OPERATOR && uiFilter.selectValues?.length) {
            result.push({
                field: staticFilter.field,
                operator: staticFilter.operator,
                value: uiFilter.selectValues.join(","),
            });
            return;
        }

        if (operator === BETWEEN_OPERATOR) {
            const { startDate, endDate } = getDateRangeFromCreatedAt(
                uiFilter.datePreset,
                uiFilter.customStartDate,
                uiFilter.customEndDate
            );
            if (startDate && endDate) {
                result.push({
                    field: staticFilter.field,
                    operator: staticFilter.operator,
                    value: `${startDate}, ${endDate}`,
                });
            }
        }
    });

    return result;
};

export const toInsightsCreatedOnRange = (uiFilters: InsightsUiFilters): InsightsDateRange => {
    const createdOnFilter = uiFilters[CREATED_ON_KEY];
    if (!isInsightsFieldActive(createdOnFilter)) {
        return DEFAULT_INSIGHTS_DATE_RANGE;
    }

    return getDateRangeFromCreatedAt(
        createdOnFilter.datePreset,
        createdOnFilter.customStartDate,
        createdOnFilter.customEndDate
    );
};

const getDateChipTitle = (filter: InsightsUiFieldFilter): string | null => {
    const { datePreset, customStartDate, customEndDate } = filter;
    if (!datePreset) return null;
    if (datePreset === CUSTOM_VALUE) {
        if (customStartDate && customEndDate) {
            return `${dayjs(customStartDate).format(DateFormat.MM_DD_YYYY)} to ${dayjs(customEndDate).format(DateFormat.MM_DD_YYYY)}`;
        }
        return null;
    }
    const option = DATE_SELECTION_OPTIONS.find((opt) => opt.value === datePreset);
    return option?.label ?? datePreset;
};

export const buildInsightsFilterChips = (filters: InsightsUiFilters): FilterChipData<string>[] => {
    const result: FilterChipData<string>[] = [];

    Object.entries(filters).forEach(([field, filter]) => {
        if (!isInsightsFieldActive(filter)) {
            return;
        }

        const label = getFilterFieldLabel(field);
        const operator = filter.operator.toLowerCase();

        if (operator === LIKE_OPERATOR && filter.textValue) {
            result.push({ key: field, label, titles: filter.textValue });
            return;
        }

        if (operator === IN_OPERATOR && filter.selectValues?.length) {
            result.push({ key: field, label, titles: filter.selectValues });
            return;
        }

        if (operator === BETWEEN_OPERATOR) {
            const dateTitle = getDateChipTitle(filter);
            if (dateTitle) {
                result.push({ key: field, label, titles: dateTitle });
            }
        }
    });

    return result;
};

export const getInsightsFilterOptions = (
    availableFilters: FilterModel[],
    draftFilters: InsightsUiFilters,
    updateDraft: (field: string, patch: Partial<InsightsUiFieldFilter>) => void
): FilterOption[] => {
    const filterOptions: FilterOption[] = [];

    const addDateOption = (field: string, operator: InsightsUiFieldFilter["operator"]) => {
        const current = draftFilters[field];
        filterOptions.push({
            label: getFilterFieldLabel(field),
            type: "date",
            options: DATE_SELECTION_OPTIONS,
            value: current?.datePreset,
            handleChange: (value) => {
                const datePreset = typeof value === "string" ? value : undefined;
                updateDraft(field, {
                    operator,
                    datePreset,
                    ...(datePreset !== CUSTOM_VALUE
                        ? { customStartDate: null, customEndDate: null }
                        : {}),
                });
            },
            customStartDate: current?.customStartDate ?? null,
            customEndDate: current?.customEndDate ?? null,
            onCustomRangeChange: (start, end) =>
                updateDraft(field, {
                    operator,
                    customStartDate: start,
                    customEndDate: end,
                }),
        });
    };

    addDateOption(CREATED_ON_KEY, BETWEEN_OPERATOR);

    availableFilters.forEach((filter) => {
        if (filter.field === CREATED_ON_KEY) {
            return;
        }

        const operator = filter.operator.toLowerCase();
        const label = getFilterFieldLabel(filter.field);
        const current = draftFilters[filter.field];

        if (operator === LIKE_OPERATOR) {
            filterOptions.push({
                label,
                type: "text",
                value: current?.textValue ?? "",
                handleChange: (value) =>
                    updateDraft(filter.field, {
                        operator: filter.operator,
                        textValue: typeof value === "string" ? value : "",
                    }),
            });
            return;
        }

        if (operator === IN_OPERATOR) {
            const options = filter.value
                ? GetSplitAndTrim(filter.value).map((item) => ({ label: item, value: item }))
                : [];
            filterOptions.push({
                label,
                type: "select",
                options,
                value: current?.selectValues ?? [],
                handleChange: (value) =>
                    updateDraft(filter.field, {
                        operator: filter.operator,
                        selectValues: Array.isArray(value) ? value : [],
                    }),
            });
            return;
        }

        if (operator === BETWEEN_OPERATOR) {
            addDateOption(filter.field, filter.operator);
        }
    });

    return filterOptions;
};
